import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
  Collapse,
} from "@mui/material";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  LayoutDashboard,
  Settings,
  DatabaseZap,
  FileVolume2,
  Tickets,
  PhoneCall,
  SquareKanban,
  ContactRound,
  ChevronDown,
  ChevronUp,
  BarChart3, // Icon for Reports
  FileText, // Icon for sub-reports
  Calendar,
  Users,
  User,
  LogOut,
  Facebook,
  Headset,
  Briefcase,
  FolderKanban,
  MessageCircle,
  ShieldAlert,
  BookOpen,
  CheckSquare,
  ListTodo,
  List as ListIcon,
  Timer,
  Tag,
  Map,
} from "lucide-react";

import { useSidebarConfig, isMenuEnabled } from "../services/sidebarService";
import ProfileImg from "../../src/assets/user.png";
import Logo from "../../src/assets/just-talk-logo.png";
import { clearSession } from "../utils/clearSession";

const DRAWER_WIDTH = 280;

export default function SideNavbar({ data }) {
  const name = localStorage.getItem("username") || "Workspace";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // State for collapsible menus
  const [openReports, setOpenReports] = useState(false);
  const [openSupport, setOpenSupport] = useState(() => /^\/(helpdesk|complaints)/.test(window.location.pathname));
  const [openPm, setOpenPm] = useState(() => /^\/(projects|pm)(\/|$)/.test(window.location.pathname));
  const [openSales, setOpenSales] = useState(() =>
    /^\/(all-tickets|single-ticket|new-lead|lead-management-system|appointment-booking|MetaLeads)/i.test(window.location.pathname)
  );

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sidebarCfg = useSidebarConfig();
  const enabled = (key) => isMenuEnabled(sidebarCfg, key);

  const isLinkActive = (path) => {
    const currentFull = location.pathname === "/lead-management-system" && !location.search
      ? "/lead-management-system?pipeline=meta"
      : location.pathname + location.search;
    return currentFull === path || location.pathname === path;
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    navigate("/");
    clearSession();
  };

  const navigationItems = [
    {
      text: "Dashboard",
      icon: <LayoutDashboard size={20} />,
      key: "dashboard",
      path: "/dashboard",
    },
    {
      text: "Solution FAQ’s",
      icon: <FileVolume2 size={20} />,
      key: "faq",
      path: "/faq",
    },
    {
      text: "Call Log",
      icon: <PhoneCall size={20} />,
      key: "call-log",
      path: "/call-log",
    },
  ];

  const salesSubItems = [
    { key: "sales.tickets", text: "Tickets", icon: <Tickets size={18} />, path: "/all-tickets" },
    { key: "sales.lead-status", text: "Lead Status", icon: <ContactRound size={18} />, path: "/new-lead" },
    { key: "sales.lead-pipeline", text: "Lead Pipeline", icon: <SquareKanban size={18} />, path: "/lead-management-system?pipeline=meta" },
    // { key: "sales.whatsapp-board", text: "WhatsApp Board", icon: <SquareKanban size={18} />, path: "/lead-management-system?pipeline=whatsapp" },
    { key: "sales.appointments", text: "Appointment Booking", icon: <Calendar size={18} />, path: "/appointment-booking" },
    { key: "sales.meta-leads", text: "Meta Leads", icon: <Facebook size={18} />, path: "/MetaLeads" },
  ];

  const bottomItems = [
    {
      text: "Team Members",
      icon: <Users size={20} />,
      key: "team-members",
      path: "/team-members",
    },
  ];

  const supportSubSections = [
    {
      title: "CUSTOMER SUPPORT",
      items: [
        { key: "support.helpdesk", text: "Helpdesk", icon: <Headset size={16} />, path: "/helpdesk/tickets" },
        { key: "support.chat", text: "Live Chat", icon: <MessageCircle size={16} />, path: "/helpdesk/chat" },
        { key: "support.complaints", text: "Complaints", icon: <ShieldAlert size={16} />, path: "/complaints" },
      ],
    },
    {
      title: "SELF SERVICE",
      items: [
        { key: "support.kb", text: "Knowledge Base", icon: <BookOpen size={16} />, path: "/helpdesk/kb" },
      ],
    },
    {
      title: "INSIGHTS",
      items: [
        { key: "support.dashboard", text: "Support Dashboard", icon: <LayoutDashboard size={16} />, path: "/helpdesk/dashboard" },
        { key: "support.reports", text: "Support Reports", icon: <BarChart3 size={16} />, path: "/helpdesk/reports" },
      ],
    },
    {
      title: "CONFIGURATION",
      items: [
        { key: "support.settings", text: "Support Settings", icon: <Settings size={16} />, path: "/helpdesk/settings" },
      ],
    },
  ];

  const pmSubSections = [
    {
      title: "WORK",
      items: [
        { key: "pm.my-work", text: "My Work", icon: <CheckSquare size={16} />, path: "/pm/my-work" },
        { key: "pm.issues", text: "My Issues", icon: <ListTodo size={16} />, path: "/pm/issues" },
      ],
    },
    {
      title: "PROJECTS",
      items: [
        { key: "pm.projects", text: "Shared Projects", icon: <FolderKanban size={16} />, path: "/projects" },
      ],
    },
    {
      title: "PLANNING",
      items: [
        { key: "pm.backlog", text: "Backlog", icon: <ListIcon size={16} />, path: "/pm/backlog" },
        { key: "pm.sprints", text: "Active Sprints", icon: <Timer size={16} />, path: "/pm/sprints" },
        { key: "pm.roadmaps", text: "Roadmaps", icon: <Map size={16} />, path: "/pm/roadmaps" },
      ],
    },
    {
      title: "DELIVERY",
      items: [
        { key: "pm.boards", text: "Boards", icon: <SquareKanban size={16} />, path: "/pm/boards" },
        { key: "pm.releases", text: "Releases", icon: <Tag size={16} />, path: "/pm/releases" },
      ],
    },
    {
      title: "INSIGHTS",
      items: [
        { key: "pm.reports", text: "Project Reports", icon: <BarChart3 size={16} />, path: "/pm/reports" },
        { key: "pm.dashboards", text: "Dashboards", icon: <LayoutDashboard size={16} />, path: "/pm/dashboards" },
      ],
    },
    {
      title: "CONFIGURATION",
      items: [
        { key: "pm.settings", text: "PM Settings", icon: <Settings size={16} />, path: "/pm/settings" },
        { key: "pm.import", text: "Import Data", icon: <DatabaseZap size={16} />, path: "/pm/import" },
      ],
    },
  ];

  const reportSubItems = [
    { key: "reports.traffic", text: "Traffic", path: "/reports/traffic" },
    { key: "reports.cdr", text: "CDR Report", path: "/reports/cdr" },
    { key: "reports.sla", text: "SLA Report", path: "/reports/sla" },
    { key: "reports.conversion", text: "Conversion Report", path: "/reports/conversion" },
    { key: "reports.unreached", text: "Leads Report", path: "/reports/unreached" },
  ];

  const renderTopItem = (item) => {
    const active = isLinkActive(item.path);
    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.path}
          sx={{
            borderRadius: 3,
            bgcolor: active ? "rgb(99, 102, 241) !important" : "transparent",
            "& .MuiListItemText-primary": {
              color: active ? "white !important" : "black",
              fontWeight: active ? 600 : 100,
            },
            "& .MuiListItemIcon-root": {
              color: active ? "white !important" : "grey",
            },
            "&:hover": {
              bgcolor: active ? "rgb(99, 102, 241)" : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
        </ListItemButton>
      </ListItem>
    );
  };

  // Collapsible section (Sales, Reports).
  const renderGroup = ({ label, icon, open, setOpen, items: allItems }) => {
    const items = allItems.filter((i) => enabled(i.key));
    if (!items.length) return null;
    return (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ borderRadius: 3, "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" } }}>
        <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
          <Box sx={{ display: "flex" }}>{icon}</Box>
        </ListItemIcon>
        <ListItemText primary={label} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem", fontWeight: 100, color: "black" } }} />
        {open ? <ChevronUp size={18} color="grey" /> : <ChevronDown size={18} color="grey" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 2 }}>
          {items.map((subItem) => {
            const active = isLinkActive(subItem.path) || (subItem.path !== "/" && location.pathname.startsWith(`${subItem.path.split("?")[0]}/`));
            return (
              <ListItemButton
                key={subItem.text}
                component={NavLink}
                to={subItem.path}
                sx={{
                  borderRadius: 3,
                  mt: 0.5,
                  bgcolor: active ? "rgba(99, 102, 241, 0.1) !important" : "transparent !important",
                  "& .MuiListItemText-primary": { color: active ? "rgb(99, 102, 241)" : "black", fontWeight: active ? 600 : 100 },
                  "& .MuiListItemIcon-root": { color: active ? "rgb(99, 102, 241)" : "grey" },
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04) !important" },
                }}
              >
                {subItem.icon && <ListItemIcon sx={{ minWidth: 32 }}>{subItem.icon}</ListItemIcon>}
                <ListItemText primary={subItem.text} sx={{ "& .MuiListItemText-primary": { fontSize: "0.8rem" } }} />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </ListItem>
    );
  };

  // Grouped Collapsible Section (Support & Project Management)
  const renderSectionedGroup = ({ label, icon, open, setOpen, sections: allSections }) => {
    const sections = allSections
      .map((sec) => ({ ...sec, items: sec.items.filter((i) => enabled(i.key)) }))
      .filter((sec) => sec.items.length);
    if (!sections.length) return null;
    return (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ borderRadius: 3, "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" } }}>
        <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
          <Box sx={{ display: "flex" }}>{icon}</Box>
        </ListItemIcon>
        <ListItemText primary={label} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem", fontWeight: 100, color: "black" } }} />
        {open ? <ChevronUp size={18} color="grey" /> : <ChevronDown size={18} color="grey" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 1.5 }}>
          {sections.map((sec, secIdx) => (
            <Box key={sec.title} sx={{ mt: secIdx === 0 ? 0.5 : 1.25 }}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "#98A2B3",
                  px: 1.5,
                  py: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {sec.title}
              </Typography>
              {sec.items.map((subItem) => {
                const active = isLinkActive(subItem.path) || (subItem.path !== "/" && location.pathname.startsWith(`${subItem.path.split("?")[0]}/`));
                return (
                  <ListItemButton
                    key={subItem.text}
                    component={NavLink}
                    to={subItem.path}
                    sx={{
                      borderRadius: 2.5,
                      mt: 0.3,
                      py: 0.6,
                      px: 1.5,
                      bgcolor: active ? "rgba(99, 102, 241, 0.12) !important" : "transparent !important",
                      "& .MuiListItemText-primary": {
                        color: active ? "rgb(99, 102, 241)" : "#344054",
                        fontWeight: active ? 600 : 500,
                        fontSize: "0.8rem",
                      },
                      "& .MuiListItemIcon-root": {
                        color: active ? "rgb(99, 102, 241)" : "#98A2B3",
                        minWidth: 28,
                      },
                      "&:hover": {
                        bgcolor: active ? "rgba(99, 102, 241, 0.16) !important" : "rgba(0, 0, 0, 0.04) !important",
                        "& .MuiListItemIcon-root": { color: active ? "rgb(99, 102, 241)" : "#475467" },
                      },
                    }}
                  >
                    {subItem.icon && <ListItemIcon>{subItem.icon}</ListItemIcon>}
                    <ListItemText primary={subItem.text} />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}
        </List>
      </Collapse>
    </ListItem>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        color: "black",
        borderRight: "1px solid #eee",
        overflow: "hidden",
      }}
    >
      {/* Top Fixed Header - Logo & Workspace */}
      <Box sx={{ flexShrink: 0 }}>
        {/* Logo */}
        <Box
          sx={{ p: "15px 25px 10px 22px", display: "flex", alignItems: "center" }}
        >
          <img
            src={Logo}
            style={{ height: 40, mixBlendMode: "hard" }}
            alt="Logo"
          />
        </Box>

        {/* Workspace Selector */}
        <Box sx={{ px: 3, mb: 3 }}>
          <Button
            fullWidth
            sx={{
              justifyContent: "space-between",
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.04)",
              borderRadius: 3,
              border: "1px solid #4B4B4B",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
            }}
          >
            <Box sx={{ textAlign: "left", p: "1px 5px" }}>
              <Typography
                variant="body2"
                color="rgba(22, 22, 22, 0.7)"
                sx={{ textTransform: "capitalize", fontWeight: 100 }}
              >
                {name}
              </Typography>
            </Box>
          </Button>
        </Box>

        <Box sx={{ borderTop: "0.5px solid #ccc", p: "5px 0 0px 0" }}></Box>
      </Box>

      {/* Middle Scrollable Navigation List */}
      <List
        sx={{
          px: 2,
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          visibility: sidebarCfg.loaded ? "visible" : "hidden", // no flash of menus that are about to be hidden
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#e2e8f0",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#cbd5e1",
          },
        }}
      >
        {navigationItems.filter((i) => enabled(i.key)).map(renderTopItem)}

        {/* --- SALES (CRM) COLLAPSIBLE SECTION --- */}
        {renderGroup({ label: "Sales", icon: <Briefcase size={20} />, open: openSales, setOpen: setOpenSales, items: salesSubItems })}

        {/* --- PROJECT MANAGEMENT COLLAPSIBLE SECTION --- */}
        {renderSectionedGroup({ label: "Project Management", icon: <FolderKanban size={20} />, open: openPm, setOpen: setOpenPm, sections: pmSubSections })}

        {/* --- SUPPORT (HELPDESK) COLLAPSIBLE SECTION --- */}
        {renderSectionedGroup({ label: "Support", icon: <Headset size={20} />, open: openSupport, setOpen: setOpenSupport, sections: supportSubSections })}

        {/* --- REPORTS COLLAPSIBLE SECTION --- */}
        {reportSubItems.some((i) => enabled(i.key)) && (
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => setOpenReports(!openReports)}
            sx={{
              borderRadius: 3,
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
              <Box sx={{ display: 'flex' }}><BarChart3 size={20} /></Box>
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 100,
                  color: "black",
                },
              }}
            />
            {openReports ? (
              <ChevronUp size={18} color="grey" />
            ) : (
              <ChevronDown size={18} color="grey" />
            )}
          </ListItemButton>

          <Collapse in={openReports} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2 }}>
              {reportSubItems.filter((i) => enabled(i.key)).map((subItem) => (
                <ListItemButton
                  key={subItem.text}
                  component={NavLink}
                  to={subItem.path}
                  sx={{
                    borderRadius: 3,
                    mt: 0.5,
                    "&.active": {
                      bgcolor: "rgba(99, 102, 241, 0.1)", // Light purple highlight
                      "& .MuiListItemText-primary": {
                        color: "rgb(99, 102, 241)",
                        fontWeight: 600,
                      },
                    },
                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemText
                    primary={subItem.text}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.8rem",
                        fontWeight: 100,
                        color: "black",
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </ListItem>
        )}

        {bottomItems.filter((i) => enabled(i.key)).map(renderTopItem)}
      </List>

      {/* Fixed Bottom Section - User Profile & Logout */}
      <Box sx={{ px: 2, pb: 2, flexShrink: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "0.5px solid #ccc", my: 1 }}></Box>
        <List disablePadding>
          {/* User Profile */}
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/profile"
              sx={{
                borderRadius: 3,
                "&.active": {
                  bgcolor: "rgb(99, 102, 241)",
                  "& .MuiListItemText-primary": {
                    color: "white",
                    fontWeight: 600,
                  },
                  "& .MuiListItemIcon-root": { color: "white" },
                  "&:hover": { bgcolor: "rgb(99, 102, 241)" },
                },
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
                <Box sx={{ display: 'flex' }}><User size={20} /></Box>
              </ListItemIcon>
              <ListItemText
                primary="User Profile"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 100,
                    color: "black",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Logout */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 3,
                "&:hover": {
                  bgcolor: "rgba(239, 68, 68, 0.08)",
                  "& .MuiListItemText-primary": { color: "#ef4444" },
                  "& .MuiListItemIcon-root": { color: "#ef4444" },
                },
              }}
            >
              <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
                <LogOut size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 100,
                    color: "black",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Mobile Header */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #eee",
            p: "5px 20px",
          }}
        >
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
            <Box sx={{ display: 'flex' }}><Menu color="black" size={24} /></Box>
          </IconButton>
          <Avatar
            src={ProfileImg}
            sx={{ width: 29, height: 29, cursor: "pointer" }}
            onClick={handleAvatarClick}
          />
        </Box>
      )}

      {/* Avatar Menu */}
      <MuiMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/profile");
          }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
        >
          Logout
        </MenuItem>
      </MuiMenu>

      {/* Drawers */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            bgcolor: "#fff",
            borderRight: "1px solid #eee",
            height: "100vh",
            maxHeight: "100vh",
            overflow: "hidden",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            bgcolor: "#fff",
            border: "none",
            borderRight: "1px solid #eee",
            height: "100vh",
            maxHeight: "100vh",
            overflow: "hidden",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
